import React, { useEffect, useState } from "react";
import { formatApiError } from "@/lib/api";
import { getCurrentSignature, fetchSignatureImage, uploadSignature } from "@/lib/signatures";
import SignatureBlock from "@/components/SignatureBlock";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Small card: current signature on file (if any) plus an "Update signature" button that opens
// SignatureBlock in a modal. Mounted in the driver portal now; admin-side mounting comes later.
export default function SignatureOnFile({ driverId, legalName, canUpdate }) {
  const [sig, setSig] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const current = await getCurrentSignature(driverId);
      setSig(current);
      if (current) {
        const url = await fetchSignatureImage(current.id);
        setImgUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } else {
        setImgUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  // Revoke the blob URL on unmount so we do not leak memory across driver switches.
  useEffect(() => () => { if (imgUrl) URL.revokeObjectURL(imgUrl); }, [imgUrl]);

  const handleAdopt = async ({ method, typedName, blob }) => {
    try {
      await uploadSignature(driverId, { method, typedName, blob });
      toast.success("Signature saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="signature-on-file">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Signature on file</div>
        {canUpdate && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn-primary text-xs px-3 py-2"
            style={{ minHeight: 44 }}
            data-testid="update-signature-btn"
          >
            {sig ? "Update signature" : "Add signature"}
          </button>
        )}
      </div>

      <div className="mt-3">
        {loading && <div className="text-xs text-slate-400">Loading…</div>}
        {!loading && !sig && <div className="text-xs text-slate-400">No signature on file yet.</div>}
        {!loading && sig && (
          <div className="flex items-center gap-3">
            <div className="border border-slate-200 rounded-md bg-white p-2 flex items-center justify-center" style={{ minWidth: 96, minHeight: 48 }}>
              {imgUrl && <img src={imgUrl} alt="Signature on file" className="max-h-12" data-testid="signature-img" />}
            </div>
            <div className="text-xs text-slate-500 capitalize" data-testid="signature-meta">
              {sig.method} · {fmtDate(sig.created_at)}
            </div>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="signature-modal">
          <div className="bg-white w-full max-w-md rounded-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold text-slate-900">Update signature</div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500 text-sm hover:text-slate-900" data-testid="signature-modal-close">
                Close
              </button>
            </div>
            <SignatureBlock legalName={legalName} onAdopt={handleAdopt} />
          </div>
        </div>
      )}
    </div>
  );
}
